import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Rail } from './Rail';
import { SubNav } from './SubNav';
import { useLightspeedNav, resolveActiveGroup } from './navConfig';
import { Button } from '@/components/ui/button';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { PausedSessionOverlay } from '@/components/PausedSessionOverlay';
import { TrialBanner } from '@/components/TrialBanner';
import { IntercomProvider } from '@/components/IntercomProvider';
import { OwnerTwoFactorDialog } from '@/components/OwnerTwoFactorDialog';
import { InactivityWarningDialog } from '@/components/InactivityWarningDialog';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useRegisterSession } from '@/hooks/useRegisterSession';
import { usePermissions } from '@/hooks/usePermissions';
import { useRealtimeReconnect } from '@/hooks/useRealtimeReconnect';
import { useSentryUser } from '@/hooks/useSentryUser';
import { cn } from '@/lib/utils';

// Below this width the icon rail + sub-nav collapse into a slide-out drawer.
const NARROW_QUERY = '(max-width: 1023px)';

/**
 * Lightspeed Retail X-Series layout: a slim left icon rail driving a
 * contextual sub-navigation column, with the routed page filling the rest.
 * On narrow windows the rail + sub-nav auto-hide behind a hamburger and open
 * as a slide-out drawer.
 */
export function Layout() {
  const { isImpersonating } = useImpersonation();
  const { effectiveRole } = usePermissions();
  const { isSessionPaused } = useRegisterSession();
  const { pathname } = useLocation();
  const [subNavCollapsed, setSubNavCollapsed] = useState(false);
  const [narrow, setNarrow] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useRealtimeReconnect();
  useSentryUser();

  // Track whether the viewport is narrow enough to collapse the nav chrome.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(NARROW_QUERY);
    const update = () => setNarrow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Close the drawer whenever the route changes (e.g. tapping a nav item).
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  const groups = useLightspeedNav();
  const activeGroup = resolveActiveGroup(groups, pathname);

  const isCashierRole = effectiveRole === 'cashier' || effectiveRole === 'employee';
  const showPausedOverlay = isCashierRole && isSessionPaused;

  const nav = (
    <>
      <Rail
        groups={groups}
        activeGroupId={activeGroup.id}
        subNavCollapsed={subNavCollapsed}
        onToggleSubNav={() => setSubNavCollapsed((v) => !v)}
      />
      {!subNavCollapsed && <SubNav group={activeGroup} />}
    </>
  );

  return (
    <IntercomProvider>
      <div className="min-h-screen bg-background text-foreground">
        <OwnerTwoFactorDialog />
        <InactivityWarningDialog />
        {showPausedOverlay ? <PausedSessionOverlay /> : null}
        <ImpersonationBanner />

        <div className={cn('flex h-screen', isImpersonating && 'pt-12')}>
          {/* Wide screens: nav is always docked on the left. */}
          {!narrow && nav}

          {/* Narrow screens: nav lives in a slide-out drawer. */}
          {narrow && drawerOpen && (
            <div className="fixed inset-0 z-50 flex">
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => setDrawerOpen(false)}
                aria-hidden="true"
              />
              <div className="relative z-10 flex h-full shadow-xl">{nav}</div>
            </div>
          )}

          <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
            {narrow && (
              <div
                className="flex items-center gap-2 border-b border-border bg-[hsl(var(--ls-rail))] px-2 shrink-0"
                style={{ height: 'var(--header-height, 56px)' }}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10"
                  onClick={() => setDrawerOpen(true)}
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
                <img src="/blazepos-logo.png" alt="BlazePOS" className="h-6 w-6 object-contain" />
                <span className="text-sm font-semibold truncate">{activeGroup.label}</span>
              </div>
            )}
            <TrialBanner />
            <main className="flex-1 min-h-0 overflow-hidden">
              <Outlet />
            </main>
          </div>
        </div>
      </div>
    </IntercomProvider>
  );
}
